<script>
  // Sync results panel for the Direct Bill review screen. Rendered inline below
  // the bill so users can see the outcome (with Xero/JobTread links) and still
  // navigate to the next selected bill.
  export let syncResults = [];
  export let selectedBills = [];
  export let overheadDest = 'Xero';
  export let userExpenseCode = '';
  export let syncing = false;
  export let accountingOnly = false;
  export let onRetry = () => {};
  // Xero already has this supplier invoice: link to that bill (then JobTread),
  // or create a second one anyway.
  export let onLinkExisting = null;
  export let onCreateAnyway = null;
  export let onClearSynced = null;
  export let clearingSynced = false;
  export let onBack = () => {};
  export let showBack = true;
</script>

<div class="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
  <h3 class="text-sm font-semibold text-gray-900 mb-2">Sync Results</h3>
  <div class="space-y-2">
    {#each syncResults as result}
      {@const fileName = selectedBills.find(b => b.uploadId === result.uploadId)?.fileName || result.uploadId}
      {@const qbo = result.quickbooks || null}
      {@const xeroSkipped = result.xero === null}
      {@const allSuccess = qbo ? !!qbo.success : ((xeroSkipped || result.xero?.success) && (result.jobtread?.success || result.accountingOnly))}
      {@const anyFail = qbo ? !qbo.success : ((!xeroSkipped && !result.xero?.success) || (result.jobtread && !result.jobtread.success))}
      {@const alreadySynced = !!(result.xero?.alreadySynced || qbo?.alreadySynced)}
      {@const isTaxTypeConflict = !qbo && !xeroSkipped && !result.xero?.success && /TaxType code .+ cannot be used with account code/i.test(result.xero?.error || '')}
      {@const conflictAccountCode = isTaxTypeConflict ? (result.xero.error.match(/account code '([^']+)'/i) || [])[1] : null}
      <div class="flex flex-col py-2 px-3 rounded gap-2 {allSuccess ? 'bg-green-50' : anyFail ? 'bg-red-50' : 'bg-amber-50'}">
        <div class="text-sm {allSuccess ? 'text-green-800' : 'text-red-800'} font-medium truncate" title={fileName}>
          {fileName}
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-1">
            {#if qbo}
              {#if qbo.success}
                <span class="text-xs text-green-600">✅ QuickBooks &rarr; {qbo.docTypeLabel || 'Bill'} {qbo.docNumber || qbo.qboBillId}
                  {#if qbo.attachedToQuickBooks}
                    <span class="text-green-500 ml-1">(document attached)</span>
                  {:else if qbo.attachmentError}
                    <span class="text-orange-500 ml-1">(attach failed)</span>
                  {/if}
                </span>
                {#if qbo.alreadyInQuickBooks}
                  <span class="text-xs text-amber-700">Matched an existing QuickBooks {(qbo.docTypeLabel || 'bill').toLowerCase()} with this number — linked to it instead of creating a duplicate.</span>
                {/if}
                {#if qbo.vendorCreated}
                  <span class="text-xs text-gray-500">Created vendor "{qbo.vendorName}" in QuickBooks</span>
                {/if}
                {#if qbo.docType === 'purchase'}
                  <span class="text-xs text-gray-500">Posted as paid — not added to Accounts Payable.</span>
                {/if}
                {#if qbo.taxTreatmentConfident === false}
                  <span class="text-xs text-amber-700">⚠️ Couldn't tell whether the line amounts include GST — posted as tax-exclusive. Check the total in QuickBooks.</span>
                {/if}
              {:else if alreadySynced}
                <span class="text-xs text-red-600">❌ Already synced from Buildzilla</span>
              {:else}
                <span class="text-xs text-red-600">❌ QuickBooks: {qbo.error || 'Failed'}</span>
              {/if}
              <span class="text-xs text-amber-700">— JobTread skipped (overhead &rarr; QuickBooks only)</span>
            {:else if xeroSkipped}
              <span class="text-xs text-gray-500">— Xero skipped (QuickBooks mode)</span>
            {:else if result.xero?.pendingLink}
              <span class="text-xs text-gray-600">Xero left unchanged — this upload is not synced yet</span>
            {:else if result.xero?.success}
              <span class="text-xs text-green-600">✅ Xero → {result.xero.xeroBill.number || 'Created'}
                {#if result.xero.linkedExisting}
                  <span class="text-green-500 ml-1">(linked to the bill already in Xero{result.xero.xeroBill.status ? `, ${result.xero.xeroBill.status}` : ''} — not created again)</span>
                {:else if result.xero.pdfAttachedToXero}
                  <span class="text-green-500 ml-1">(PDF attached)</span>
                {:else if result.xero.pdfAttachmentError}
                  <span class="text-orange-500 ml-1">(PDF attach failed)</span>
                {/if}
              </span>
              {#if result.xero.xeroBill?.tracking?.length}
                {#each result.xero.xeroBill.tracking as entry}
                  <span class="text-xs text-purple-600">Tracking: {entry.categoryName} = {entry.option}</span>
                {/each}
              {/if}
              {#if result.xero.trackingWarning}
                <span class="text-xs text-amber-700">⚠️ {result.xero.trackingWarning}</span>
              {/if}
              {#if result.xero.taxWarning}
                <span class="text-xs text-amber-700">⚠️ {result.xero.taxWarning}</span>
              {/if}
            {:else if alreadySynced}
              <span class="text-xs text-red-600">❌ Already synced from Buildzilla</span>
            {:else}
              <span class="text-xs text-red-600">❌ Xero sync failed</span>
            {/if}
            {#if result.jobtread?.success}
              <span class="text-xs text-green-600">✅ JobTread → Synced
                {#if result.jobtread.pdfAttachedToJT}
                  <span class="text-green-500 ml-1">(PDF attached)</span>
                {:else if result.jobtread.pdfAttachmentError}
                  <span class="text-orange-500 ml-1">(PDF attach failed)</span>
                {/if}
              </span>
            {:else if result.accountingOnly}
              <span class="text-xs text-amber-700">— JobTread skipped (overhead → {overheadDest} only)</span>
            {:else if result.jobtread}
              <span class="text-xs text-red-600">❌ JobTread: {result.jobtread.error || 'Failed'}</span>
            {:else if alreadySynced}
              <span class="text-xs text-gray-500">— JobTread sync skipped</span>
            {:else if !result.xero?.success}
              <span class="text-xs text-gray-500">— JobTread sync skipped (Xero failed)</span>
            {/if}
          </div>
          <div class="flex flex-wrap items-center gap-2">
            {#if qbo?.success && qbo.url}
              <a
                href={qbo.url}
                target="_blank"
                rel="noopener noreferrer"
                class="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded font-medium transition-colors duration-200 whitespace-nowrap no-underline"
                style="color: white !important;"
              >
                Open in QuickBooks &rarr;
              </a>
            {/if}
            {#if result.jobtread?.success && result.jobtread.url}
              <a
                href={result.jobtread.url}
                target="_blank"
                rel="noopener noreferrer"
                class="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded font-medium transition-colors duration-200 whitespace-nowrap no-underline"
                style="color: white !important;"
              >
                Open in JobTread →
              </a>
            {/if}
            {#if result.xero?.success}
              <a
                href={result.xero.xeroBill.url}
                target="_blank"
                rel="noopener noreferrer"
                class="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded font-medium transition-colors duration-200 whitespace-nowrap no-underline"
                style="color: white !important;"
              >
                Open in Xero →
              </a>
            {/if}
          </div>
        </div>

        {#if alreadySynced}
          <div class="ml-0 mt-1 p-3 bg-white border border-red-200 rounded">
            <p class="text-sm text-red-800 font-medium mb-1">This bill was already synced from Buildzilla.</p>
            <p class="text-xs text-gray-600 mb-3">
              Voiding it in Xero or deleting it in JobTread does not reset that.
              Clear the synced status if you want to push it again — that creates a new bill, it does not revive the old one.
            </p>
            {#if onClearSynced}
              <button
                on:click={() => onClearSynced(result.uploadId)}
                disabled={syncing || clearingSynced}
                class="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {clearingSynced ? 'Clearing…' : 'Clear synced status'}
              </button>
            {/if}
          </div>
        {:else if qbo && !qbo.success}
          <div class="ml-0 mt-1 p-3 bg-white border border-red-200 rounded">
            <p class="text-sm text-red-800 font-medium mb-1">QuickBooks returned an error:</p>
            <p class="text-xs text-red-700 font-mono bg-red-50 p-2 rounded">{qbo.error || 'Unknown error'}</p>
          </div>
        {:else if !qbo && !xeroSkipped && !result.xero?.success}
          <div class="ml-0 mt-1 p-3 bg-white border border-red-200 rounded">
            {#if isTaxTypeConflict}
              <p class="text-sm text-red-800 font-medium mb-1">
                Xero sync failed because the tax setting on account code "{conflictAccountCode}" doesn't support the selected tax type.
              </p>
              <p class="text-xs text-gray-600 mb-2">
                The mapped account code doesn't allow the tax type required for this bill.
              </p>
              {#if userExpenseCode}
                <button
                  on:click={onRetry}
                  disabled={syncing}
                  class="px-3 py-1.5 text-xs font-medium bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncing ? 'Retrying...' : `Retry sync with default expense code (${userExpenseCode})`}
                </button>
              {/if}
              <p class="text-xs text-gray-500 mt-2">
                To fix permanently, review your <a href="/settings" class="text-blue-600 hover:text-blue-800 underline">Xero Chart of Account mappings in Settings</a>.
              </p>
            {:else}
              {#if result.xero?.pendingLink}
                <p class="text-sm text-red-800 font-medium mb-1">Not marked synced — you can try again.</p>
                <p class="text-xs text-red-700 font-mono bg-red-50 p-2 rounded">{result.jobtread && !result.jobtread.success ? (result.jobtread.error || result.xero?.error) : (result.xero?.error || 'Unknown error')}</p>
              {:else}
                <p class="text-sm text-red-800 font-medium mb-1">Xero returned an error:</p>
                <p class="text-xs text-red-700 font-mono bg-red-50 p-2 rounded">{result.xero?.error || 'Unknown error'}</p>
              {/if}
              {#if result.xero?.duplicateSupplierInvoice && (onLinkExisting || onCreateAnyway)}
                <div class="mt-3 flex flex-wrap gap-2">
                  {#if onLinkExisting && result.xero.existing?.id && result.xero.existing?.count === 1}
                    <button
                      on:click={() => onLinkExisting(result.xero.existing.id)}
                      disabled={syncing}
                      class="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {syncing ? 'Working…' : accountingOnly ? 'Use the bill already in Xero' : 'Use the bill already in Xero → add to JobTread'}
                    </button>
                  {/if}
                  {#if onCreateAnyway}
                    <button
                      on:click={onCreateAnyway}
                      disabled={syncing}
                      class="px-3 py-1.5 text-xs font-medium bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {syncing ? 'Working…' : 'Create anyway (a second bill in Xero)'}
                    </button>
                  {/if}
                </div>
                {#if onLinkExisting && result.xero.existing?.count > 1}
                  <p class="text-xs text-gray-600 mt-2">Xero has {result.xero.existing.count} matching bills, so it can't tell which one to use. Remove the extra one in Xero, then sync again.</p>
                {:else if onLinkExisting && !accountingOnly}
                  <p class="text-xs text-gray-600 mt-2">"Use the bill already in Xero" leaves Xero as it is and creates the JobTread bill against it{String(result.xero.existing?.status || '').toUpperCase() === 'PAID' ? ', marked paid' : ''}.</p>
                {/if}
              {/if}
            {/if}
          </div>
        {/if}
      </div>
    {/each}
  </div>
  {#if showBack}
    <div class="mt-4 flex justify-end">
      <button on:click={onBack} class="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors duration-200">← Back to Upload</button>
    </div>
  {/if}
</div>
